import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError, subscribeSSE, type TipSummary } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { teamDe } from "@/lib/teamNames";
import { Button } from "@/components/ui/button";
import { Countdown } from "@/components/Countdown";
import { LiveBadge } from "@/components/LiveBadge";
import { CountUp } from "@/components/CountUp";
import { TipHeatmap } from "@/components/TipHeatmap";

import { calcPoints } from "@/lib/scoring";
import { formatKickoff, isLocked, lockTime } from "@/lib/time";
import { ChevronLeft, ChevronRight, Lock, Minus, Plus, Share2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { toast } from "sonner";

// Maps an API game (camelCase) to the snake_case shape this page renders against.
function normalizeGame(g: any): any {
  if (!g) return g;
  return {
    ...g,
    home_team: g.homeTeam, away_team: g.awayTeam,
    home_flag: g.homeFlag, away_flag: g.awayFlag,
    home_score: g.homeScore, away_score: g.awayScore,
    live_home_score: g.liveHomeScore, live_away_score: g.liveAwayScore,
    is_finished: g.isFinished, is_live: g.isLive,
    kickoff_time: g.kickoffTime,
    group_label: g.groupLabel,
    status_short: g.statusShort,
    game_no: g.gameNo,
  };
}

export default function GameDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const nav = useNavigate();
  const [game, setGame] = useState<any>(null);
  const [tip, setTip] = useState<any>(null);
  const [home, setHome] = useState(0);
  const [away, setAway] = useState(0);
  // Aggregated community-tip summary (outcome counts + score distribution).
  // Replaces the old full-tips download — payload is bounded by distinct
  // scores, not tipper count, so it stays tiny at 10k+ users.
  const [summary, setSummary] = useState<TipSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  // Siblings for prev/next navigation. All games sorted by kickoff_time,
  // plus the IDs of games the current user has already tipped — so the
  // "Next" target can prefer untipped games.
  const [siblings, setSiblings] = useState<{ id: string; kickoff_time: string; phase: string }[]>([]);
  const [myTippedIds, setMyTippedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    (async () => {
      const [g, sum, allGamesRaw, mineTipsRaw] = await Promise.all([
        api.games.byId(id),
        api.tips.summary(id),
        // Lightweight list for siblings + own tips so we can route to
        // prev/next without an extra round-trip on every navigation.
        api.games.list() as Promise<any[]>,
        user ? (api.tips.listMine() as Promise<any[]>) : Promise.resolve([] as any[]),
      ]);
      if (!mounted) return;
      const normalized = normalizeGame(g);
      setGame(normalized);
      setSummary(sum);
      const mine = sum.myTip;
      if (mine) {
        setTip({ home_tip: mine.homeTip, away_tip: mine.awayTip, points_earned: mine.pointsEarned });
        setHome(mine.homeTip);
        setAway(mine.awayTip);
      } else {
        // Reset draft on game change so a previous game's local edit
        // doesn't leak into a new game whose tip hasn't loaded yet.
        setTip(null);
        setHome(0);
        setAway(0);
      }
      // Siblings: just the fields needed to navigate. Real WM + imports
      // + test games — we keep all of them; the user might want to flip
      // through any visible fixture.
      const sib = (allGamesRaw ?? [])
        .map((x: any) => ({ id: x.id as string, kickoff_time: x.kickoffTime as string, phase: x.phase as string }))
        .sort((a: any, b: any) => new Date(a.kickoff_time).getTime() - new Date(b.kickoff_time).getTime());
      setSiblings(sib);
      setMyTippedIds(new Set((mineTipsRaw ?? []).map((t: any) => t.gameId)));
      if (normalized) document.title = `${teamDe(normalized.home_team)} vs. ${teamDe(normalized.away_team)} – Tippspiel`;
    })();

    // SSE replaces supabase.channel — receives both tip:upsert / tip:delete and
    // game:update / game:live events for this game.
    const close = subscribeSSE(`/sse/games/${id}`, {
      onMessage: (evt) => {
        if (evt?.type === "game:update" || evt?.type === "game:live") {
          if (evt.game) setGame((prev: any) => ({ ...prev, ...normalizeGame(evt.game) }));
        }
        if (evt?.type === "tip:upsert" || evt?.type === "tip:delete") {
          // Refresh community tip distribution. Jittered so thousands of
          // clients don't refetch in the same instant; the endpoint is
          // server-cached for 10s anyway.
          setTimeout(() => {
            api.tips.summary(id!).then((s) => setSummary(s)).catch(() => {});
          }, Math.random() * 5000);
        }
      },
    });
    return () => {
      mounted = false;
      close();
    };
  }, [id, user]);

  const locked = game ? isLocked(game.kickoff_time) : false;
  const finished = game?.is_finished && game?.home_score != null;
  const live = !!game?.is_live && !finished;
  const dispHome = live ? (game?.live_home_score ?? 0) : (game?.home_score ?? 0);
  const dispAway = live ? (game?.live_away_score ?? 0) : (game?.away_score ?? 0);
  const livePoints = live && tip ? calcPoints(dispHome, dispAway, tip.home_tip, tip.away_tip) : 0;

  // Prev/next navigation through the fixture list. "prev" / "next" are
  // strict chronological neighbours; "nextUntipped" is the smarter
  // forward target — first game after the current one that the user
  // hasn't tipped yet AND that isn't already locked. Falls back to the
  // plain chronological next when everything ahead is tipped or locked.
  const { prevId, nextId, nextUntippedId } = useMemo(() => {
    if (!id || siblings.length === 0) return { prevId: null, nextId: null, nextUntippedId: null };
    const idx = siblings.findIndex((s) => s.id === id);
    if (idx < 0) return { prevId: null, nextId: null, nextUntippedId: null };
    const prev = idx > 0 ? siblings[idx - 1].id : null;
    const next = idx < siblings.length - 1 ? siblings[idx + 1].id : null;
    let untipped: string | null = null;
    for (let i = idx + 1; i < siblings.length; i++) {
      const s = siblings[i];
      if (myTippedIds.has(s.id)) continue;
      if (isLocked(s.kickoff_time)) continue; // skip already-locked games
      untipped = s.id;
      break;
    }
    return { prevId: prev, nextId: next, nextUntippedId: untipped ?? next };
  }, [id, siblings, myTippedIds]);

  const stats = useMemo(() => {
    if (!summary || summary.total === 0) return null;
    const { total, home: h, draw: d, away: a, dist } = summary;
    // dist arrives sorted by count desc — first entry is the most-tipped score.
    const top: [string, number] | undefined = dist[0] ? [`${dist[0].h}:${dist[0].a}`, dist[0].count] : undefined;
    return { hPct: Math.round(h/total*100), dPct: Math.round(d/total*100), aPct: Math.round(a/total*100), top, total };
  }, [summary]);

  const submit = async () => {
    if (!user || !id) return;
    setBusy(true);
    try {
      await api.tips.upsert(id, home, away);
      setTip({ home_tip: home, away_tip: away });
      setJustSaved(true);
      // Mark this game tipped locally so the next-untipped computation
      // skips it without waiting for a global tips refetch.
      setMyTippedIds((prev) => {
        if (prev.has(id)) return prev;
        const n = new Set(prev);
        n.add(id);
        return n;
      });
      confetti({ particleCount: 60, spread: 60, origin: { y: 0.7 } });
      toast.success("Tipp abgegeben! 🎯");
      // justSaved drives the button-morph into "Weiter zum nächsten Spiel"
      // for a few seconds so the user can keep flying through fixtures.
      setTimeout(() => setJustSaved(false), 4000);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Fehler beim Speichern";
      toast.error(/gesperrt/i.test(msg) || (err instanceof ApiError && err.status === 423) ? "Tipps sind gesperrt! 🔒" : msg);
    } finally {
      setBusy(false);
    }
  };

  const share = () => {
    const txt = `Mein Tipp für ${teamDe(game.home_team)} vs ${teamDe(game.away_team)}: ${home}:${away} ⚽\nTipp jetzt mit beim Englputzeder Tippspiel: ${window.location.origin}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank");
  };

  if (!game) return <div className="py-20 text-center text-muted-foreground">Lade Spiel …</div>;

  return (
    <div className="space-y-4">
      {/* Nav strip: back + prev/next siblings. Lets tippers fly through
          the fixture list without going back to the dashboard. The
          forward button uses `nextUntippedId` so it prefers the next
          ungetipptes Spiel; falls back to chronological next if all
          upcoming have been tipped. */}
      <div className="flex items-center justify-between gap-2">
        <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-muted-foreground tap">
          <ChevronLeft className="h-4 w-4" /> Zurück
        </button>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => prevId && nav(`/spiel/${prevId}`)}
            disabled={!prevId}
            title="Vorheriges Spiel"
            className="inline-flex items-center gap-1 text-xs font-bold text-muted-foreground bg-card border border-border rounded-pill px-2.5 py-1.5 tap disabled:opacity-40"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Vorh.
          </button>
          <button
            onClick={() => nextUntippedId && nav(`/spiel/${nextUntippedId}`)}
            disabled={!nextUntippedId}
            title={nextUntippedId === nextId ? "Nächstes Spiel" : "Nächstes ungetipptes Spiel"}
            className="inline-flex items-center gap-1 text-xs font-bold text-accent-foreground bg-accent rounded-pill px-2.5 py-1.5 tap disabled:opacity-40"
          >
            Nächstes <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Match header */}
      <div className="bg-hero text-primary-foreground rounded-lg shadow-pop p-5 text-center relative overflow-hidden">
        <div className="text-[10px] font-bold uppercase tracking-wider text-primary-foreground/80">
          Spiel {game.game_no} · {game.phase === "group" ? `Gruppe ${game.group_label}` : (game.group_label || "KO")}
        </div>
        <div className="grid grid-cols-3 items-center gap-2 mt-3">
          <div>
            <div className="text-5xl">{game.home_flag}</div>
            <div className="text-sm font-bold mt-1">{teamDe(game.home_team)}</div>
          </div>
          <div className="font-display font-extrabold text-3xl text-primary-foreground">
            {(finished || live) ? (
              <span className="inline-flex items-center gap-1"><CountUp value={dispHome} />:<CountUp value={dispAway} /></span>
            ) : "vs"}
          </div>
          <div>
            <div className="text-5xl">{game.away_flag}</div>
            <div className="text-sm font-bold mt-1">{teamDe(game.away_team)}</div>
          </div>
        </div>
        <div className="text-xs mt-3 opacity-80">
          {formatKickoff(game.kickoff_time)}{game.venue && <> · {game.venue}</>}
        </div>
        {live && (
          <div className="mt-3 flex items-center justify-center gap-2">
            <LiveBadge minute={game.minute} status={game.status_short} />
            {tip && (
              <span className={`text-xs font-bold rounded-pill px-2.5 py-1 ${livePoints > 0 ? "bg-gold text-foreground" : "bg-primary-foreground/20 text-primary-foreground"}`}>
                {livePoints > 0 ? `+${livePoints} ✨ Live-Punkte` : "0 Live-Punkte"}
              </span>
            )}
          </div>
        )}
        {!finished && !live && (
          <div className="mt-3">
            {locked ? (
              <span className="inline-flex items-center gap-1 bg-accent text-accent-foreground rounded-pill px-3 py-1 text-xs font-bold">
                <Lock className="h-3 w-3" /> Tipps gesperrt
              </span>
            ) : (
              <Countdown target={lockTime(game.kickoff_time)} />
            )}
          </div>
        )}
      </div>

      {/* Tip input */}
      {!finished && (
        <div className="bg-card rounded-lg shadow-card p-5">
          <h2 className="font-display font-bold text-lg mb-3">Dein Tipp 🎯</h2>
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
            <Stepper value={home} setValue={setHome} disabled={locked} flag={game.home_flag} />
            <div className="font-display font-extrabold text-3xl">:</div>
            <Stepper value={away} setValue={setAway} disabled={locked} flag={game.away_flag} />
          </div>

          <div className="mt-4 text-center text-xs text-muted-foreground">
            Exakt: <b className="text-foreground">+4</b> · Tordifferenz: <b className="text-foreground">+3</b> · Tendenz: <b className="text-foreground">+2</b>
          </div>

          {!locked && (
            <Button
              onClick={submit} disabled={busy}
              className="w-full h-12 mt-4 bg-accent text-accent-foreground hover:bg-accent/90 font-bold text-base tap relative overflow-hidden"
            >
              <AnimatePresence>
                {justSaved && (
                  <motion.span
                    initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} exit={{ scale: 0 }}
                    className="absolute inset-0 flex items-center justify-center text-2xl"
                  >⚽ ✓</motion.span>
                )}
              </AnimatePresence>
              <span className={justSaved ? "opacity-0" : ""}>{tip ? "Tipp aktualisieren 🔁" : "Tipp abgeben 🎯"}</span>
            </Button>
          )}

          {/* "Weiter zum nächsten Spiel" — appears as soon as the user
              has tipped this game and there's still another fixture
              ahead. Heavy attention animation right after save so users
              don't overlook it:
                • Slide up + scale bounce on entry
                • Pulse glow ring during the `justSaved` window
                • Continuous gentle arrow nudge while idle (chevron loop)
              Once `justSaved` resets the highlight fades but the chevron
              keeps nudging subtly until the user clicks. */}
          {tip && nextUntippedId && (
            <motion.div
              key={nextUntippedId /* re-run entrance when the target changes */}
              initial={{ opacity: 0, y: 16, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.05 }}
              className="relative mt-2"
            >
              {/* Pulse halo while justSaved — fades out automatically. */}
              <AnimatePresence>
                {justSaved && (
                  <motion.div
                    aria-hidden
                    initial={{ opacity: 0.0, scale: 0.96 }}
                    animate={{ opacity: [0, 0.55, 0], scale: [0.98, 1.08, 1.14] }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.4, repeat: 2, ease: "easeOut" }}
                    className="absolute inset-0 rounded-md bg-success/50 blur-md pointer-events-none"
                  />
                )}
              </AnimatePresence>

              <motion.div
                // Subtle bouncing wiggle for the first few seconds, then
                // calms down so the page stays readable.
                animate={justSaved ? { y: [0, -4, 0, -3, 0] } : { y: 0 }}
                transition={justSaved
                  ? { duration: 0.9, repeat: 3, ease: "easeInOut" }
                  : { duration: 0.2 }}
                className="relative"
              >
                <Button
                  variant="default"
                  onClick={() => nav(`/spiel/${nextUntippedId}`)}
                  className="w-full h-12 bg-success text-white hover:bg-success/90 font-bold text-base tap gap-2 shadow-pop"
                >
                  <span>
                    {nextUntippedId === nextId
                      ? "Weiter zum nächsten Spiel"
                      : "Weiter zum nächsten ungetippten Spiel"}
                  </span>
                  {/* Arrow keeps nudging right indefinitely so the
                      "tap me" affordance never fully disappears. */}
                  <motion.span
                    animate={{ x: [0, 6, 0] }}
                    transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }}
                    className="inline-flex"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </motion.span>
                </Button>
              </motion.div>
            </motion.div>
          )}


          {tip && (
            <Button variant="outline" onClick={share} className="w-full h-11 mt-2 tap">
              <Share2 className="h-4 w-4 mr-2" /> Auf WhatsApp teilen
            </Button>
          )}
        </div>
      )}

      {/* Result */}
      {finished && tip && (
        <div className="bg-card rounded-lg shadow-card p-5 text-center">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Dein Tipp</div>
          <div className="font-display font-extrabold text-3xl mt-1">{tip.home_tip}:{tip.away_tip}</div>
          <div className={`mt-3 inline-block rounded-pill px-4 py-2 font-bold ${
            tip.points_earned === 4 ? "bg-gold" :
            tip.points_earned === 3 ? "bg-accent text-accent-foreground" :
            tip.points_earned === 2 ? "bg-secondary text-secondary-foreground" : "bg-muted text-muted-foreground"
          }`}>
            {tip.points_earned === 4 ? "Volltreffer! +4 🎯" :
             tip.points_earned === 3 ? "Tordifferenz! +3 ⚽" :
             tip.points_earned === 2 ? "Tendenz! +2 ✓" :
             "Diesmal nichts. 0 Pkt"}
          </div>
        </div>
      )}

      {/* Community stats */}
      {stats && (
        <div className="bg-card rounded-lg shadow-card p-5">
          <h3 className="font-display font-bold text-base mb-3">Community-Tipps · {stats.total} Tipper</h3>
          <div className="flex h-8 rounded-pill overflow-hidden text-xs font-bold">
            {stats.hPct > 0 && <div style={{width:`${stats.hPct}%`}} className="bg-accent text-accent-foreground flex items-center justify-center">{game.home_flag} {stats.hPct}%</div>}
            {stats.dPct > 0 && <div style={{width:`${stats.dPct}%`}} className="bg-muted text-muted-foreground flex items-center justify-center">X {stats.dPct}%</div>}
            {stats.aPct > 0 && <div style={{width:`${stats.aPct}%`}} className="bg-secondary text-secondary-foreground flex items-center justify-center">{game.away_flag} {stats.aPct}%</div>}
          </div>
          {stats.top && (
            <div className="text-xs text-muted-foreground mt-3 text-center">
              Meistgetipptes Ergebnis: <b className="text-foreground">{stats.top[0]}</b> ({stats.top[1]} Tipper)
            </div>
          )}
        </div>
      )}

      {/* Heatmap: immer sichtbar sobald Tipps existieren */}
      {summary && summary.total > 0 && (
        <>
          <TipHeatmap gameId={id!} homeFlag={game.home_flag} awayFlag={game.away_flag} globalSummary={summary} />
          {!tip && !locked && (
            <div className="text-center text-[11px] text-muted-foreground px-3">
              💡 Lass dich nicht beeinflussen — vertrau deinem Bauchgefühl.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stepper({ value, setValue, disabled, flag }: { value: number; setValue: (n:number)=>void; disabled?: boolean; flag: string }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="text-3xl">{flag}</div>
      <div className="flex items-center gap-2">
        <button
          type="button" disabled={disabled || value <= 0}
          onClick={() => setValue(Math.max(0, value-1))}
          className="h-11 w-11 rounded-pill bg-muted disabled:opacity-30 tap flex items-center justify-center"
        ><Minus className="h-5 w-5" /></button>
        <div className="font-display font-extrabold text-4xl tabular-nums w-12 text-center">{value}</div>
        <button
          type="button" disabled={disabled || value >= 15}
          onClick={() => setValue(Math.min(15, value+1))}
          className="h-11 w-11 rounded-pill bg-accent text-accent-foreground disabled:opacity-30 tap flex items-center justify-center"
        ><Plus className="h-5 w-5" /></button>
      </div>
    </div>
  );
}
